<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-setting"></i>
          <span>{{ $t('resourceManagement.quotaPolicyTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" v-if="hasPermission('sys:resource:allocate')">
            <i class="el-icon-plus"></i> {{ $t('common.add') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="queryParams" class="filter-form" @submit.native.prevent>
          <el-form-item :label="$t('resourceManagement.userLevel')">
            <el-select v-model="queryParams.userLevel" clearable :placeholder="$t('common.selectPlaceholder')" @change="handleSearch" class="filter-input">
              <el-option v-for="item in levelOptions" :key="item.levelCode" :label="item.levelCode + ' ' + item.levelName" :value="item.levelCode"></el-option>
            </el-select>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 主体内容 -->
    <el-card class="list-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="policyList"
        height="100%"
        style="width: 100%"
        class="custom-table"
        :element-loading-text="$t('common.loading')"
      >
        <el-table-column prop="name" :label="$t('resourceManagement.policyName')" align="center" min-width="150"></el-table-column>
        <el-table-column prop="userLevel" :label="$t('resourceManagement.userLevel')" align="center" width="120">
          <template slot-scope="scope">
            <el-tag :type="getLevelTag(scope.row.userLevel)" size="small" effect="plain">{{ getLevelText(scope.row.userLevel) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="initialQuota" :label="$t('resourceManagement.initQuota')" align="center" width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.isUnlimited === 1" class="unlimited-text">{{ $t('resourceManagement.unlimited') }}</span>
            <span v-else>{{ formatNumber(scope.row.initialQuota) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="resetQuota" :label="$t('resourceManagement.resetQuota')" align="center" width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.isUnlimited === 1" class="unlimited-text">{{ $t('resourceManagement.unlimited') }}</span>
            <span v-else>{{ formatNumber(scope.row.resetQuota) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="resetCycle" :label="$t('resourceManagement.resetCycle')" align="center" width="120">
          <template slot-scope="scope">
            <el-tag size="mini" type="info" effect="plain">{{ getCycleText(scope.row.resetCycle) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="allowedModels" :label="$t('resourceManagement.allowedModels')" align="center" min-width="150">
          <template slot-scope="scope">
            <div v-if="scope.row.allowedModels === '*'" class="unlimited-text">{{ $t('resourceManagement.allModels') }}</div>
            <div v-else-if="scope.row.allowedModels">
              <el-tag v-for="model in scope.row.allowedModels.split(',')" :key="model" size="mini" style="margin-right: 5px" effect="plain">
                {{ $t('resourceManagement.' + model) !== 'resourceManagement.' + model ? $t('resourceManagement.' + model) : model }}
              </el-tag>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="isUnlimited" :label="$t('resourceManagement.isUnlimited')" align="center" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isUnlimited === 1 ? 'success' : 'info'" size="mini" effect="dark">
              {{ scope.row.isUnlimited === 1 ? $t('common.yes') : $t('common.no') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.action')" align="center" :width="actionColumnWidth" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" size="mini" plain icon="el-icon-edit" @click="handleEdit(scope.row)" v-if="hasPermission('sys:resource:allocate')">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-delete" @click="handleDelete(scope.row)" v-if="hasPermission('sys:resource:allocate')">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryParams.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="queryParams.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px" @close="resetForm" custom-class="custom-dialog">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px" class="dialog-content">
        <el-form-item :label="$t('resourceManagement.policyName')" prop="name">
          <el-input v-model="form.name" :placeholder="$t('common.pleaseInput')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('resourceManagement.userLevel')" prop="userLevel">
          <el-select v-model="form.userLevel" :placeholder="$t('common.selectPlaceholder')" style="width: 100%">
            <el-option v-for="item in levelOptions" :key="item.levelCode" :label="item.levelCode + ' ' + item.levelName" :value="item.levelCode"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('resourceManagement.allowedModels')" prop="allowedModelsArr">
          <el-select v-model="form.allowedModelsArr" multiple :placeholder="$t('resourceManagement.selectModelsPlaceholder')" style="width: 100%" clearable>
            <el-option :label="$t('resourceManagement.allModels')" value="*"></el-option>
            <el-option
              v-for="item in modelOptions"
              :key="item.id"
              :label="item.modelName"
              :value="item.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('resourceManagement.isUnlimited')">
          <el-switch v-model="form.isUnlimited" :active-value="1" :inactive-value="0"></el-switch>
        </el-form-item>
        <template v-if="form.isUnlimited !== 1">
          <el-form-item :label="$t('resourceManagement.initQuota')" prop="initialQuota">
            <el-input-number v-model="form.initialQuota" :min="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('resourceManagement.resetQuota')" prop="resetQuota">
            <el-input-number v-model="form.resetQuota" :min="0" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('resourceManagement.resetCycle')" prop="resetCycle">
            <el-select v-model="form.resetCycle" style="width: 100%">
              <el-option :label="$t('resourceManagement.none')" value="none"></el-option>
              <el-option :label="$t('resourceManagement.daily')" value="daily"></el-option>
              <el-option :label="$t('resourceManagement.weekly')" value="weekly"></el-option>
              <el-option :label="$t('resourceManagement.monthly')" value="monthly"></el-option>
              <el-option :label="$t('resourceManagement.quarterly')" value="quarterly"></el-option>
              <el-option :label="$t('resourceManagement.yearly')" value="yearly"></el-option>
            </el-select>
          </el-form-item>
        </template>
        <el-form-item :label="$t('common.remark')">
          <el-input type="textarea" v-model="form.remark" :rows="3"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { getQuotaPolicies, saveQuotaPolicy, updateQuotaPolicy, deleteQuotaPolicies, getAvailableModels } from '@/apis/resource/resource'
import { getLevelOptions } from '@/apis/system/level'

export default {
  name: 'QuotaPolicy',
  data() {
    return {
      loading: false,
      submitLoading: false,
      dialogVisible: false,
      dialogTitle: '',
      policyList: [],
      modelOptions: [],
      levelOptions: [],
      total: 0,
      queryParams: {
        page: 1,
        limit: 10,
        userLevel: undefined
      },
      form: {
        id: undefined,
        name: '',
        userLevel: 'V0',
        allowedModelsArr: ['*'],
        isUnlimited: 0,
        initialQuota: 1000,
        resetQuota: 0,
        resetCycle: 'none',
        remark: ''
      },
      rules: {
        name: [{ required: true, message: this.$t('common.pleaseInput'), trigger: 'blur' }],
        userLevel: [{ required: true, message: this.$t('common.selectPlaceholder'), trigger: 'change' }],
        initialQuota: [{ required: true, message: this.$t('common.pleaseInput'), trigger: 'blur' }],
        resetQuota: [{ required: true, message: this.$t('common.pleaseInput'), trigger: 'blur' }],
        resetCycle: [{ required: true, message: this.$t('common.selectPlaceholder'), trigger: 'change' }]
      }
    }
  },
  created() {
    this.getList()
    this.fetchModels()
    this.fetchLevelOptions()
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:resource:allocate')) count += 1
      if (this.hasPermission('sys:resource:allocate')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    }
  },
  methods: {
    hasPermission,
    fetchLevelOptions() {
      getLevelOptions().then(res => {
        this.levelOptions = res || []
      }).catch(() => {})
    },
    async fetchModels() {
      try {
        const res = await getAvailableModels()
        this.modelOptions = res
      } catch (error) {
        console.error('Fetch models failed:', error)
      }
    },
    async getList() {
      this.loading = true
      try {
        const res = await getQuotaPolicies(this.queryParams)
        this.policyList = res.list
        this.total = res.total
      } catch (error) {
        console.error('Fetch quota policies failed:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.queryParams.page = 1
      this.getList()
    },
    handleReset() {
      this.queryParams.userLevel = undefined
      this.handleSearch()
    },
    handleAdd() {
      this.resetForm()
      this.dialogTitle = this.$t('common.add')
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.resetForm()
      this.dialogTitle = this.$t('common.edit')
      this.form = { ...row }
      // 处理 allowedModelsArr
      if (row.allowedModels) {
        if (row.allowedModels === '*') {
          this.$set(this.form, 'allowedModelsArr', ['*'])
        } else {
          this.$set(this.form, 'allowedModelsArr', row.allowedModels.split(','))
        }
      } else {
        this.$set(this.form, 'allowedModelsArr', [])
      }
      this.dialogVisible = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm(this.$t('common.batchDeleteConfirm', { count: 1 }), this.$t('common.tips'), {
          type: 'warning'
        })
        await deleteQuotaPolicies([row.id])
        this.$message.success(this.$t('common.deleteSuccess'))
        this.getList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Delete policy failed:', error)
        }
      }
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.submitLoading = true
          try {
            // 处理 allowedModelsArr 转为逗号分隔字符串
            if (this.form.allowedModelsArr && this.form.allowedModelsArr.length > 0) {
              if (this.form.allowedModelsArr.includes('*')) {
                this.form.allowedModels = '*'
              } else {
                this.form.allowedModels = this.form.allowedModelsArr.join(',')
              }
            } else {
              this.form.allowedModels = ''
            }

            if (this.form.id) {
              await updateQuotaPolicy(this.form)
            } else {
              await saveQuotaPolicy(this.form)
            }
            this.$message.success(this.$t('common.saveSuccess'))
            this.dialogVisible = false
            this.getList()
          } catch (error) {
            console.error('Save policy failed:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    resetForm() {
      this.form = {
        id: undefined,
        name: '',
        userLevel: 'V0',
        allowedModelsArr: ['*'],
        isUnlimited: 0,
        initialQuota: 1000,
        resetQuota: 0,
        resetCycle: 'none',
        remark: ''
      }
      if (this.$refs.form) {
        this.$refs.form.resetFields()
      }
    },
    getCycleText(cycle) {
      const cycles = {
        'none': this.$t('resourceManagement.none'),
        'daily': this.$t('resourceManagement.daily'),
        'weekly': this.$t('resourceManagement.weekly'),
        'monthly': this.$t('resourceManagement.monthly'),
        'quarterly': this.$t('resourceManagement.quarterly'),
        'yearly': this.$t('resourceManagement.yearly')
      }
      return cycles[cycle] || cycle
    },
    handleSizeChange(val) {
      this.queryParams.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryParams.page = val
      this.getList()
    },
    getLevelText(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item ? (item.levelCode + ' ' + item.levelName) : (level || this.$t('common.unknown'))
    },
    getLevelTag(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      if (item && item.levelColor) return ''
      const tags = {
        'V0': 'info', 'V1': 'info', 'V2': '',
        'V3': 'success', 'V4': 'success',
        'V5': 'warning', 'V6': 'warning',
        'V7': 'danger', 'V8': 'danger', 'V9': 'danger', 'V10': 'danger'
      }
      return tags[level] || 'info'
    },
    formatNumber(num) {
      return num ? num.toLocaleString() : 0
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 配额策略页面特有样式 */
.list-page-container {
  .filter-form {
    .filter-input { width: 220px; }
  }

  .unlimited-text {
    color: #10b981;
    font-weight: 600;
  }
}

.custom-dialog {
  border-radius: 12px;
  :deep(.el-dialog__header) {
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    .el-dialog__title { font-weight: 600; }
  }
  :deep(.el-dialog__body) { padding: 24px; }
  :deep(.el-dialog__footer) { padding: 16px 24px; border-top: 1px solid #f1f5f9; background-color: #f8fafc; }
  .dialog-content {
    .el-form-item {
      margin-bottom: 20px;
      :deep(.el-form-item__label) { font-size: 13px; font-weight: 500; color: #64748b; padding-bottom: 8px; }
    }
    :deep(.el-input__inner), :deep(.el-textarea__inner) { border-radius: 8px; border-color: #e2e8f0; }
  }
}
</style>
